import React, { useState } from 'react'

import { Box, Button, Text, Touchable } from '@factorialco/deprecated-design-system'
import {
  EditSpendingPolicyAssigneeFragment,
  FinanceDtosSpendingPoliciesPeopleGroupStrategyEnum,
  Maybe,
  UpdateSpendingPolicyAssigneesDocument,
  UpdateSpendingPolicyAssigneesMutationVariables,
} from 'generated/resources/graphql'

import RadioInput from 'modules/core/components/Inputs/FilterSelect/SelectOption/RadioInput'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'

type Strategy = Extract<
  FinanceDtosSpendingPoliciesPeopleGroupStrategyEnum,
  'manual_selection'
> | null

interface Props {
  spendingPolicyId: number
  assignees: Maybe<EditSpendingPolicyAssigneeFragment>
  onSuccess?: () => void
}

export const SpendingPolicyAssigneesForm: React.FC<Props> = ({
  spendingPolicyId,
  assignees,
  onSuccess,
}) => {
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const [strategy, setStrategy] = useState<Strategy>((assignees?.strategy as Strategy) ?? null)
  const [selectedAccessIds, setSelectedAccessIds] = useState<number[]>(assignees?.accessIds ?? [])

  const [updateSpendingPolicyAssignees] = useMutation(UpdateSpendingPolicyAssigneesDocument, {
    extractorFn: (data) => data.financeMutations.updateFinanceSpendingPolicySpendingPolicyAssignee,
  })

  const { isSubmitting, setErrors, setFieldValue, handleSubmit, isValid, dirty } =
    useForm<UpdateSpendingPolicyAssigneesMutationVariables>({
      enableReinitialize: true,

      defaultValues: {
        spendingPolicyId: spendingPolicyId,
        assignees:
          assignees === null
            ? null
            : {
                accessIds: assignees?.accessIds,
                legalEntityIds: assignees?.legalEntityIds,
              },
      },

      onSubmit: async (formValues: UpdateSpendingPolicyAssigneesMutationVariables, submitProps) => {
        let handled = null

        const result = await updateSpendingPolicyAssignees({
          variables: formValues,
        })
        handled = handleResult(result, (submitErrors) => {
          if (submitErrors) {
            setErrors(submitErrors)
            app.setFlash({
              type: 'wrong',
              message: i18n.t('spending_policies.details.form.update_error'),
            })
          }
        })

        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('spending_policies.details.form.update_success'),
          })

          submitProps.resetForm()

          onSuccess?.()
        }

        return handled
      },
    })

  return (
    <Box gap="s24">
      <Box gap="s16">
        {allCompanySelectionBox()}

        {manualSelectionBox()}
      </Box>
      {dirty && (
        <Box alignItems="flexEnd">
          <Button loading={isSubmitting} onClick={() => handleSubmit()} disabled={!isValid}>
            {i18n.t('common.submit')}
          </Button>
        </Box>
      )}
    </Box>
  )

  function allCompanySelectionBox() {
    const choosen = strategy === null

    return (
      <Box
        gap="s16"
        border={{
          all: {
            width: 's2',
            style: 'solid',
            color: choosen ? 'viridian1000' : 'grey400',
          },
        }}
        borderRadius={{
          all: 'abs008',
        }}
        padding="s16"
        background="grey000White"
      >
        <Box flexDirection="row" justifyContent="spaceBetween">
          <Text.Normal weight="semibold">
            {i18n.t('spending_policies.details.people_group.all_employees_option.title')}
          </Text.Normal>
          <Touchable
            onClick={() => {
              setStrategy(null)
              setFieldValue('assignees', null)
            }}
            autofocus={choosen}
          >
            <RadioInput selected={choosen} />
          </Touchable>
        </Box>
        <Text.Normal>
          {i18n.t('spending_policies.details.people_group.all_employees_option.description')}
        </Text.Normal>
      </Box>
    )
  }

  function manualSelectionBox() {
    const choosen = strategy === 'manual_selection'

    return (
      <Box
        background="grey000White"
        borderRadius={{
          all: 'abs008',
        }}
      >
        <Box
          gap="s16"
          border={{
            all: {
              width: 's2',
              style: 'solid',
              color: choosen ? 'viridian1000' : 'grey400',
            },
          }}
          borderRadius={{
            all: 'abs008',
          }}
          padding="s16"
          background="grey000White"
        >
          <Box flexDirection="row" justifyContent="spaceBetween">
            <Text.Normal weight="semibold">
              {i18n.t('spending_policies.details.people_group.selected_employees_option.title')}
            </Text.Normal>
            <Touchable onClick={() => setStrategy('manual_selection')} autofocus={choosen}>
              <RadioInput selected={choosen} />
            </Touchable>
          </Box>
          <Text.Normal>
            {i18n.t('spending_policies.details.people_group.selected_employees_option.description')}
          </Text.Normal>
        </Box>

        {choosen && (
          <Box padding="s16">
            <EmployeeSelectorV2
              multiple
              useAccessId
              value={selectedAccessIds}
              onChange={(state: EmployeeSelectorV2MultiSelectState) => {
                if (state.type === 'manual') {
                  setFieldValue('assignees.accessIds', state.ids)
                  setSelectedAccessIds(state.ids)
                }
              }}
              options={{ asList: true, disableSelectAll: true }}
            />
          </Box>
        )}
      </Box>
    )
  }
}
